import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { RefreshCw, Search, Trash2, Activity } from "lucide-react";
import { Pagination, usePagination } from "@/components/Pagination";
import ConfirmDelete from "@/components/ConfirmDelete";

const ROLE_BADGE = {
  team_leader: "bg-black text-white",
  manager: "bg-amber-100 text-amber-900",
  member: "bg-blue-100 text-blue-900",
};

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [emails, setEmails] = useState([]);
  const [filterEmail, setFilterEmail] = useState("all");
  const [filterMethod, setFilterMethod] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "1000");
      if (filterEmail !== "all") params.set("user_email", filterEmail);
      if (filterMethod !== "all") params.set("method", filterMethod);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const { data } = await api.get(`/audit-logs?${params.toString()}`);
      setLogs(data.logs);
      setEmails(data.user_emails);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterEmail, filterMethod, dateFrom, dateTo]);

  const filtered = logs.filter((l) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (l.action || "").toLowerCase().includes(needle)
      || (l.user_email || "").toLowerCase().includes(needle)
      || (l.user_role || "").toLowerCase().includes(needle);
  });
  const [pageItems, ctrl] = usePagination(filtered, 50);

  const clearAll = async () => {
    try {
      await api.delete("/audit-logs");
      toast.success("Audit log dibersihkan");
      setConfirmClear(false);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const resetFilters = () => {
    setFilterEmail("all"); setFilterMethod("all");
    setDateFrom(""); setDateTo(""); setQ("");
  };

  return (
    <div className="space-y-8" data-testid="audit-logs-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mini">Audit Trail</div>
          <h1 className="mt-1 font-heading text-4xl font-black tracking-tight">Riwayat Aktivitas</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Semua aksi yang dilakukan user tercatat otomatis. Halaman ini hanya untuk Team Leader dan Manager.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={load} disabled={loading} className="rounded-sm" data-testid="refresh-audit-button">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button variant="destructive" onClick={() => setConfirmClear(true)} className="rounded-sm" data-testid="clear-audit-button">
            <Trash2 className="h-4 w-4 mr-2" /> Bersihkan Log
          </Button>
        </div>
      </div>

      <div className="card-tech p-4 grid grid-cols-1 md:grid-cols-6 gap-3">
        <div>
          <Label className="label-mini">User</Label>
          <Select value={filterEmail} onValueChange={setFilterEmail}>
            <SelectTrigger data-testid="filter-user"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua user</SelectItem>
              {emails.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-mini">Jenis</Label>
          <Select value={filterMethod} onValueChange={setFilterMethod}>
            <SelectTrigger data-testid="filter-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              <SelectItem value="GET">Lihat</SelectItem>
              <SelectItem value="POST">Tambah</SelectItem>
              <SelectItem value="PATCH">Ubah</SelectItem>
              <SelectItem value="DELETE">Hapus</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-mini">Dari Tanggal</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="audit-date-from" />
        </div>
        <div>
          <Label className="label-mini">Sampai Tanggal</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="audit-date-to" />
        </div>
        <div className="md:col-span-2">
          <Label className="label-mini">Cari aktivitas / email / role</Label>
          <div className="relative flex gap-2">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground pointer-events-none" />
              <Input
                className="pl-9"
                placeholder="Contoh: 'Menambahkan project'"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="filter-query"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              className="rounded-sm shrink-0"
              onClick={resetFilters}
              data-testid="audit-reset-filters"
            >Reset</Button>
          </div>
        </div>
      </div>

      <div className="card-tech p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-accent text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-6 py-3">Waktu</th>
                <th className="text-left px-6 py-3">User</th>
                <th className="text-left px-6 py-3">Role</th>
                <th className="text-left px-6 py-3">Aktivitas</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={4} className="px-6 py-10 text-center text-muted-foreground">
                  <Activity className="inline h-4 w-4 mr-2" /> Belum ada log yang cocok.
                </td></tr>
              )}
              {pageItems.map((l) => (
                <tr key={l.id} className="border-t border-border hover:bg-accent/50" data-testid={`audit-row-${l.id}`}>
                  <td className="px-6 py-3 mono text-xs whitespace-nowrap">{new Date(l.timestamp).toLocaleString("id-ID")}</td>
                  <td className="px-6 py-3 mono text-xs">{l.user_email || "(anon)"}</td>
                  <td className="px-6 py-3 text-xs">
                    {l.user_role && (
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm ${ROLE_BADGE[l.user_role] || "bg-slate-200 text-slate-900"}`}>
                        {l.user_role}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3">{l.action || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination ctrl={ctrl} testidPrefix="audit-pg" />
      </div>

      <ConfirmDelete
        open={confirmClear}
        onOpenChange={setConfirmClear}
        onConfirm={clearAll}
        title="Bersihkan seluruh audit log?"
        description="Semua entri audit akan dihapus permanen. Aksi ini tidak dapat dibatalkan."
        testIdPrefix="confirm-clear-audit"
      />
    </div>
  );
}
